import debounce from 'lodash/debounce'
import { SANITY_SHOPIFY_GIFT_CARD_SLUG } from '../../src/constants'
import {
  getDocumentTextColor,
  getDocumentBackgroundColors
} from '~/util/sanity/document-color'
import { colors as recessColors } from '~/recess.json'
import getDocumentPath from '~/util/sanity/get-document-path'
import shopifyQuery from '~/shopify/queries/product.gql'
import sanityQuery from '~/sanity/queries/product'
import categoryQuery from '~/sanity/queries/product-category'
import testimonialsQuery from '~/sanity/queries/testimonials'
import homeQuery from '~/sanity/queries/home'

// maps a route name to a hardcoded category slug
const ROUTE_NAME_CATEGORY_MAP = {
  'shop-sparkling-water-product': 'sparkling-water',
  'shop-recess-mood-product': 'recess-mood',
  'shop-mood-powder-tub-product': 'mood-powder-tubs',
  'shop-powder-stick-pack-product': 'powder-stick-pack',
  'shop-realitywear-product': 'realitywear',
  'shop-gift-card': 'gift-cards',
  'shop-zero-proof-product': 'zero-proof'
}

function getCategorySlug(params, routeName) {
  return params.category || ROUTE_NAME_CATEGORY_MAP[routeName]
}

// maps a route name to a hardcoded product slug
// useful when we have a single product category
const ROUTE_NAME_PRODUCT_MAP = {
  'shop-gift-card': SANITY_SHOPIFY_GIFT_CARD_SLUG
}

function getProductSlug(params, routeName) {
  return params.product || ROUTE_NAME_PRODUCT_MAP[routeName]
}

export default {
  async asyncData({
    env,
    route,
    params,
    error,
    query,
    $sanity,
    $sentry,
    store
  }) {
    const slug = getProductSlug(params, route.name)
    if (!slug) {
      return error({
        statusCode: 404,
        message: "Product doesn't exist"
      })
    }

    const category = getCategorySlug(params, route.name)
    if (!category) {
      return error({
        statusCode: 404,
        message: "Category doesn't exist"
      })
    }

    // start of code ripped from pages/index.vue
    // used just for press module
    let queryId = query.preview
    if (env.nonCBDSite) {
      queryId = 'home-non-cbd'
    }
    let home = {}
    try {
      home = await $sanity.fetch(homeQuery(queryId))
      if (!home) {
        throw new Error('Category not found')
      }
    } catch (e) {
      $sentry.captureException(e)
    }

    const featuredPress = home.featuredPress ? home.featuredPress : []

    // end of code ripped from pages/index.vue

    const previewId = query.preview
    try {
      const [product, testimonials, categoryData] = await Promise.all([
        $sanity.fetch(sanityQuery({ category, slug }, previewId)),
        $sanity.fetch(testimonialsQuery({ category })),
        $sanity.fetch(categoryQuery(category))
      ])

      if (!product || (env.nonCBDSite && product.cbd)) {
        throw new Error('Product not found')
      }
      const path = getDocumentPath(product)
      let backgroundColors = getDocumentBackgroundColors(product)
      if (!backgroundColors) {
        backgroundColors = [
          recessColors['default-background-start'],
          recessColors['default-background-end']
        ]
      }
      const colors = {
        backgroundColors,
        textColor: getDocumentTextColor(product)
      }
      store.commit('screen/setPageColors', colors)

      const { categoryPartial } = product

      return {
        title: product.title,
        poster: product.poster,
        favicon: product.favicon,
        featuredPress,
        product: { ...product, testimonials },
        path,
        showTestimonials:
          categoryPartial?.showTestimonials === undefined
            ? true
            : categoryPartial?.showTestimonials,
        showPress:
          categoryPartial?.showPress === undefined
            ? true
            : categoryPartial?.showPress,
        colors,
        ingredientOverrides: {
          title: categoryData?.ingredients?.title,
          subTitle: categoryData?.ingredients?.subTitle
        }
      }
    } catch (e) {
      $sentry.captureException(e)
      return error({
        statusCode: 404,
        message: e.message
      })
    }
  },

  apollo: {
    shopifyProduct: {
      query: shopifyQuery,
      client: 'storefront',
      variables() {
        return {
          id: this.product.shopify.product.id
        }
      },
      update({ node }) {
        return node
      },
      skip() {
        const product = this.product || {}
        const shopify = product.shopify
        return !shopify || !product.shopify.product
      }
    }
  },

  data: () => ({
    product: null,
    path: null,
    colors: null
  }),

  watch: {
    shopifyProduct(product) {
      if (process.client && product) {
        this.debouncedTrackView()
      }
    }
  },

  created() {
    this.debouncedTrackView = debounce(this.trackView, 250)
  },

  mounted() {
    const { categoryPartial } = this.product

    if (categoryPartial) {
      const { backgroundImages } = categoryPartial

      if (backgroundImages && backgroundImages.length >= 1) {
        this.$store.commit('screen/setBackgroundImages', backgroundImages)
      }
    }

    if (this.shopifyProduct) {
      this.debouncedTrackView()
    }
  },

  beforeDestroy() {
    this.$store.commit('screen/setBackgroundImages', [])
  },

  methods: {
    trackView() {
      // klaviyo
      var klaviyo = window.klaviyo || [] // eslint-disable-line
      const { product, shopifyProduct } = this
      const item = {
        ProductName: product.title,
        Categories: [product.category],
        ImageURL: product.shopify?.product?.image,
        ProductID: shopifyProduct?.id,
        Brand: 'Recess',
        Price: shopifyProduct?.priceRange?.minVariantPrice.amount,
        URL: `${process.env.canonicalUrl}${this.$route.path}`
      }
      klaviyo.push(['track', 'Viewed Product', item])
      klaviyo.push([
        'trackViewedItem',
        {
          Title: item.ProductName,
          ItemId: item.ProductID,
          Categories: item.Categories,
          ImageUrl: item.ImageURL,
          Url: item.URL
        }
      ])
    }
  }
}
